import React, { useState, useRef, useEffect } from 'react';

export interface HeroSectionProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
  categories: string[];
  categoryCounts: Record<string, number>;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategorySelect,
  categories,
  categoryCounts,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Top quick categories (excluding 'All')
  const topCategories = categories.filter(c => c !== 'All').slice(0, 5);
  const moreCategories = categories.filter(c => c !== 'All').slice(5);

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#FFF6ED] via-[#FDFBF7] to-[#FFF1E4] dark:from-[#1E1B18] dark:via-[#16171B] dark:to-[#221B16] border border-orange-100/80 dark:border-gray-800/80 p-6 sm:p-8 lg:p-10 shadow-soft mb-8 transition-colors duration-300">
      {/* Decorative ambient background blur */}
      <div className="absolute top-0 right-1/4 w-72 h-72 bg-orange-300/15 dark:bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-60 h-60 bg-amber-200/20 dark:bg-amber-600/10 rounded-full blur-2xl pointer-events-none" />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Left Column: Heading, Subtitle, Search & Chips */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          {/* Main Headline */}
          <div className="relative inline-block mb-3">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white tracking-tight leading-tight">
                Find Your
              </h1>
              {/* Three orange sparkle/accent ticks */}
              <div className="flex gap-1 text-orange-500 transform -rotate-12 select-none">
                <span className="w-1 h-3.5 bg-orange-500 rounded-full inline-block" />
                <span className="w-1 h-4 bg-orange-500 rounded-full inline-block" />
                <span className="w-1 h-3.5 bg-orange-500 rounded-full inline-block" />
              </div>
            </div>

            <div className="font-handwriting text-3xl sm:text-4xl lg:text-[44px] font-bold text-orange-600 dark:text-orange-400 leading-tight">
              People, Places & Services
            </div>

            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 dark:text-white flex items-center gap-2">
              <span>in</span>
              <span className="font-handwriting text-3xl sm:text-4xl lg:text-5xl text-orange-600 dark:text-orange-400 font-bold underline decoration-wavy decoration-orange-400/60 decoration-2">
                Bangalore
              </span>
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 max-w-xl mb-6">
            Food, business, services and more — curated by the Gujarati community.
          </p>

          {/* Search Input Bar */}
          <div className="relative max-w-2xl mb-5">
            <div className="relative flex items-center bg-white dark:bg-gray-800 rounded-full shadow-soft-lg border border-orange-200/70 dark:border-gray-700 focus-within:border-orange-500 dark:focus-within:border-orange-500 focus-within:ring-4 focus-within:ring-orange-500/10 transition-all duration-200 pl-4 pr-2 py-1.5">
              <div className="text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0">
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search by name, area, category or keyword..."
                className="w-full bg-transparent text-sm sm:text-base text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none py-2"
              />

              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  aria-label="Clear search"
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full mr-1 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              <button
                type="button"
                aria-label="Search"
                className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white flex items-center justify-center flex-shrink-0 shadow-sm transition-transform active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </div>

          {/* Quick Filter Category Chips */}
          <div className="flex flex-wrap items-center gap-2 relative" ref={dropdownRef}>
            {/* All Categories Pill / Trigger */}
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 border transition-all duration-200 shadow-sm ${
                selectedCategory === 'All'
                  ? 'bg-orange-500 text-white border-orange-500 shadow-orange-500/20'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-orange-300'
              }`}
            >
              <span>{selectedCategory === 'All' ? 'All Categories' : selectedCategory}</span>
              <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Quick Categories */}
            {topCategories.map((cat) => {
              const active = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => onCategorySelect(active ? 'All' : cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                    active
                      ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-orange-50/70 dark:hover:bg-gray-700/50 hover:border-orange-200'
                  }`}
                >
                  {cat}
                  {categoryCounts[cat] !== undefined && (
                    <span className="ml-1 opacity-70 text-[10px]">({categoryCounts[cat]})</span>
                  )}
                </button>
              );
            })}

            {/* More Categories Dropdown (if any) */}
            {moreCategories.length > 0 && (
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-orange-50/70 dark:hover:bg-gray-700/50"
              >
                More ▾
              </button>
            )}

            {/* Dropdown Menu for All / More Categories */}
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-soft-xl border border-gray-200 dark:border-gray-700 z-50 p-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    onCategorySelect('All');
                    setDropdownOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold flex items-center justify-between transition-colors ${
                    selectedCategory === 'All'
                      ? 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span>All Categories</span>
                  <span className="text-gray-400">({categoryCounts['All'] || 0})</span>
                </button>

                <div className="h-px bg-gray-100 dark:bg-gray-700 my-1" />

                {categories
                  .filter(c => c !== 'All')
                  .map((cat) => (
                    <button
                      key={cat}
                      onClick={() => {
                        onCategorySelect(cat);
                        setDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 rounded-xl text-xs font-medium flex items-center justify-between transition-colors ${
                        selectedCategory === cat
                          ? 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 font-bold'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <span className="truncate">{cat}</span>
                      <span className="text-gray-400 ml-2 text-[10px]">
                        ({categoryCounts[cat] || 0})
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Bangalore Skyline Illustration Banner */}
        <div className="lg:col-span-5 hidden lg:block relative">
          <div className="relative rounded-2xl overflow-hidden shadow-soft-xl border-4 border-white dark:border-gray-800 aspect-[16/10] group">
            <img
              src="/assets/bengaluru-skyline.jpg"
              alt="Namma Bengaluru Cityscape with Namma Metro and Skyline"
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
            />
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

            {/* Namma Bengaluru tag in top right */}
            <div className="absolute top-3 right-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md px-3 py-1 rounded-full shadow-sm">
              <span className="font-handwriting text-sm font-bold text-gray-800 dark:text-gray-200">
                Namma Bengaluru ❤️
              </span>
            </div>

            {/* Bottom info banner */}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-xs font-medium">
              <span className="bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-lg">
                Curated Community Directory
              </span>
              <span className="bg-orange-500/90 backdrop-blur-sm px-2.5 py-1 rounded-lg text-white font-semibold">
                Bengaluru Hub
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
